// components/WishlistPopover.jsx
import React from "react";
import { Link } from "react-router-dom";
import {
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Divider,
  Button,
} from "@mui/material";
import { Favorite, DeleteOutline, ShoppingBagOutlined, Close } from "@mui/icons-material";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";

export default function WishlistPopover({ anchorEl, onClose }) {
  const open = Boolean(anchorEl);
  const { wishlistItems, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();


  const handleAddToCart = (item) => {
    addToCart(item);
    removeFromWishlist(item.id);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        sx: {
          width: 360,
          p: 2,
          maxHeight: 500,
          overflowY: "auto",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid #eee",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Favorite color="error" fontSize="small" /> Wishlist ({wishlistItems.length})
        </Typography>
        {wishlistItems.length > 0 && (
          <IconButton onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        )}
      </Box>

      {wishlistItems.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Favorite sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <Typography color="text.secondary">
            Your wishlist is empty
          </Typography>
          <Button
            component={Link}
            to="/products"
            size="small"
            variant="outlined"
            sx={{ mt: 2 }}
          >
            Browse products
          </Button>
        </Box>
      ) : (
        <>
          <List disablePadding>
            {wishlistItems.map((item) => (
              <React.Fragment key={item.id}>
                <ListItem
                  sx={{
                    py: 1,
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                  secondaryAction={
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 0.5,
                        alignItems: "center",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleAddToCart(item)}
                        title="Move to cart"
                        sx={{ p: 0.75 }}
                      >
                        <ShoppingBagOutlined fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => removeFromWishlist(item.id)}
                        title="Remove"
                        sx={{ p: 0.75 }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemAvatar sx={{ minWidth: 68 }}>
                    <Avatar
                      src={item.image}
                      alt={item.name || item.title}
                      variant="rounded"
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 1,
                        border: "1px solid #eee",
                        objectFit: "cover",
                      }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        fontWeight="medium"
                        noWrap
                        sx={{ maxWidth: { xs: "60vw", sm: "70vw" } }}
                        title={item.name || item.title}
                      >
                        {item.name || item.title}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        USD{" "}
                        {(
                          typeof item.price === "string"
                            ? parseFloat(item.price)
                            : item.price
                        ).toFixed(2)}
                      </Typography>
                    }
                    sx={{ pr: 6 }} // reserve space so text doesn't collide with actions
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>

          <Box mt={2} display="flex" justifyContent="center">
            <Button
              component={Link}
              to="/wishlist"
              variant="outlined"
              fullWidth
              sx={{
                mt: 1,
                py: 1.25,
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "0.875rem",
                borderRadius: 1,
                borderColor: "primary.main",
                color: "primary.main",
                "&:hover": {
                  borderColor: "primary.dark",
                  color: "primary.dark",
                  bgcolor: "grey.50",
                },
              }}
              onClick={onClose}
            >
              View all
            </Button>
          </Box>
        </>
      )}
    </Popover>
  );
}